import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "item",
    "column",
    "list",
    "emptyState",
    "promptState",
    "panel",
  ]

  static values = {
    selectable: { type: Boolean, default: false },
  }

  connect() {
    this.selectedAppKey = ""
    this.manualAppSelection = false
    this.filter()
    this.onListsLoaded = (event) => {
      if (
        event.target.id === "add_step_suggested_apps_list" ||
        event.target.id === "add_step_all_apps_list"
      ) {
        this.filter()
      }
    }
    this.element.addEventListener("turbo:frame-load", this.onListsLoaded)
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.onListsLoaded)
  }

  filter(event) {
    const query = event?.target?.value ?? this.searchQuery()
    this.applyFilter(query)
  }

  backToApps(event) {
    event.preventDefault()

    const frame = this.element.closest("turbo-frame")
    if (!frame) return

    frame.src = event.currentTarget.href
  }

  selectApp(event) {
    if (!this.selectableValue) return

    const item = event.currentTarget.closest("[data-picker-filter-target='item']")
    if (!item || item.classList.contains("hidden")) return

    this.selectedAppKey = item.dataset.appKey || ""
    this.manualAppSelection = true
    this.updateSelection()
  }

  searchQuery() {
    if (!this.hasSearchInputTarget) return ""

    return this.searchInputTarget.value
  }

  applyFilter(query) {
    const normalizedQuery = query.trim().toLowerCase()

    this.itemTargets.forEach((item) => {
      if (!this.isSearchFilterable(item)) return

      const name = (item.dataset.searchName || item.textContent || "").toLowerCase()
      const isVisible = normalizedQuery.length === 0 || name.includes(normalizedQuery)
      item.classList.toggle("hidden", !isVisible)
    })

    this.updateColumns()
    this.updateSelection()
  }

  isSearchFilterable(item) {
    if (item.closest("[data-picker-filter-role='triggers']")) return false
    if (item.closest("[data-picker-filter-target='panel']")) return false

    return true
  }

  updateColumns() {
    const anyItemVisible = this.itemTargets.some(
      (item) =>
        this.isSearchFilterable(item) && !item.classList.contains("hidden"),
    )

    this.columnTargets.forEach((column) => {
      if (column.dataset.pickerFilterRole === "triggers") {
        this.updateTriggersColumn(column, anyItemVisible)
        return
      }

      this.updateColumn(column)
    })
  }

  updateTriggersColumn(column, anyItemVisible) {
    const list = column.querySelector("[data-picker-filter-target='list']")
    if (list) list.classList.toggle("hidden", !anyItemVisible)
  }

  updateColumn(column) {
    const items = this.itemsIn(column).filter((item) => this.isSearchFilterable(item))
    const visibleCount = items.filter((item) => !item.classList.contains("hidden")).length
    const list = column.querySelector("[data-picker-filter-target='list']")
    const emptyState = column.querySelector("[data-picker-filter-target='emptyState']")

    if (list) list.classList.toggle("hidden", visibleCount === 0)
    if (emptyState) emptyState.classList.toggle("hidden", visibleCount > 0)
  }

  updateSelection() {
    if (!this.selectableValue) return

    const query = this.searchQuery().trim().toLowerCase()
    const visibleApps = this.visibleSearchableApps()

    if (!this.manualAppSelection) {
      if (query.length > 0 && visibleApps.length === 1) {
        this.selectedAppKey = visibleApps[0].dataset.appKey || ""
      } else {
        this.selectedAppKey = ""
      }
    }

    const selected = this.itemTargets.find(
      (item) => item.dataset.appKey === this.selectedAppKey,
    )
    const selectionValid =
      selected && !selected.classList.contains("hidden")

    if (!selectionValid) {
      this.selectedAppKey = ""
      this.manualAppSelection = false
    }

    this.itemTargets.forEach((item) => {
      item.classList.toggle(
        "picker-app-row--selected",
        selectionValid && item.dataset.appKey === this.selectedAppKey,
      )
    })

    this.panelTargets.forEach((panel) => {
      const show =
        selectionValid && panel.dataset.appKey === this.selectedAppKey
      panel.classList.toggle("hidden", !show)
    })

    this.promptStateTargets.forEach((prompt) => {
      prompt.classList.toggle("hidden", selectionValid)
    })
  }

  visibleSearchableApps() {
    return this.itemTargets.filter(
      (item) =>
        this.isSearchFilterable(item) &&
        !item.classList.contains("hidden") &&
        item.dataset.appKey,
    )
  }

  itemsIn(column) {
    return Array.from(
      column.querySelectorAll("[data-picker-filter-target='item']"),
    )
  }
}
